import { consumptionHooks } from '$src/lib/trpcHooks'
import {
  Alert,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { useForm, zodResolver } from '@mantine/form'
import { AppModal } from '@markku/components'
import type { Consumption } from '@markku/types'
import { formatDate } from '@markku/utils'
import { IconInfoCircle } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { z } from 'zod'
import { CONSUMPTION_TYPES, useConsumptionCalculation } from './utils'

interface ConsumptionModalProps {
  mode: 'add' | 'edit'
  opened: boolean
  onClose: () => void
  propertyId: number
  allConsumptions: Consumption[]
  consumption?: Consumption // Required for edit mode
  defaultType?: 'electricity' | 'water' | 'heating' // Default type for add mode
}

const formSchema = z.object({
  type: z.enum(['electricity', 'water', 'heating']),
  date: z.string(),
  meterReading: z.number().nonnegative('Mittarilukeman on oltava positiivinen'),
  unit: z.string(),
  costEuros: z.number().optional(),
  notes: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

export function ConsumptionModal({
  mode,
  opened,
  onClose,
  propertyId,
  allConsumptions,
  consumption,
  defaultType = 'electricity',
}: ConsumptionModalProps) {
  const { mutateAsync: createConsumption, isPending: isCreating } =
    consumptionHooks.useCreate()
  const { mutateAsync: updateConsumption, isPending: isUpdating } =
    consumptionHooks.useUpdate()

  const form = useForm<FormValues>({
    validate: zodResolver(formSchema),
    initialValues:
      mode === 'edit' && consumption
        ? {
            type: consumption.type,
            date: consumption.date,
            meterReading: Number(consumption.meterReading),
            unit: consumption.unit,
            costEuros: consumption.cost
              ? parseFloat(consumption.cost)
              : undefined,
            notes: consumption.notes || undefined,
          }
        : {
            type: defaultType,
            date: dayjs().format('YYYY-MM-DD'),
            meterReading: '' as any, // Blank input instead of 0
            unit:
              CONSUMPTION_TYPES.find((t) => t.value === defaultType)?.unit ||
              'kWh',
            costEuros: undefined,
            notes: undefined,
          },
  })

  // Calculate consumption based on previous readings
  const calculation = useConsumptionCalculation({
    meterReading: form.values.meterReading,
    type: form.values.type,
    allConsumptions,
    currentDate: mode === 'edit' ? consumption?.date : undefined,
  })

  // Update unit when type changes
  useEffect(() => {
    const selectedType = CONSUMPTION_TYPES.find(
      (t) => t.value === form.values.type,
    )
    if (selectedType) {
      form.setFieldValue('unit', selectedType.unit)
    }
  }, [form.values.type])

  const handleSubmit = async (values: FormValues) => {
    const payload = {
      propertyId,
      type: values.type,
      date: values.date,
      unit: values.unit,
      meterReading: values.meterReading.toString(),
      cost: values.costEuros?.toString(),
      notes: values.notes,
    }
    if (mode === 'edit' && consumption) {
      await updateConsumption({
        id: consumption.id,
        data: payload,
      })
    } else {
      await createConsumption({
        data: payload,
      })
    }
    form.reset()
    onClose()
  }

  const isPending = isCreating || isUpdating
  const title =
    mode === 'edit' ? 'Muokkaa mittarilukemaa' : 'Lisää mittarilukema'

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      confirmProps={{
        label: 'Tallenna',
        onClick: form.onSubmit(handleSubmit),
        loading: isPending,
      }}
      cancelProps={{
        label: 'Peruuta',
      }}
    >
      <form>
        <Stack gap="md">
          <Select
            label="Kulutustyyppi"
            placeholder="Valitse tyyppi"
            data={CONSUMPTION_TYPES}
            required
            {...form.getInputProps('type')}
          />

          <DateInput
            label="Päivämäärä"
            placeholder="Valitse päivä"
            required
            valueFormat="DD.MM.YYYY"
            value={form.values.date ? dayjs(form.values.date).toDate() : null}
            onChange={(value) => {
              form.setFieldValue(
                'date',
                value ? dayjs(value).format('YYYY-MM-DD') : '',
              )
            }}
            error={form.errors.date}
          />

          <NumberInput
            label="Mittarilukema"
            description="Syötä mittarin nykyinen lukema. Kulutus lasketaan automaattisesti edellisestä lukemasta."
            placeholder="0.0"
            required
            decimalScale={2}
            min={0}
            step={0.1}
            rightSection={<Text size="sm">{form.values.unit}</Text>}
            rightSectionWidth={60}
            {...form.getInputProps('meterReading')}
          />

          {/* Show context about previous reading */}
          {(mode === 'add' || !calculation.isFirst) && (
            <Alert
              icon={<IconInfoCircle size={16} />}
              color={calculation.isFirst ? 'blue' : 'green'}
              variant="light"
            >
              {calculation.isFirst ? (
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    Ensimmäinen mittarilukema
                  </Text>
                  <Text size="sm">
                    Tämä toimii lähtöpisteenä. Seuraava lukema näyttää
                    kulutuksen.
                  </Text>
                </Stack>
              ) : (
                <Text size="sm">
                  Edellinen lukema: {calculation.previousReading.toFixed(1)}{' '}
                  {form.values.unit} ({formatDate(calculation.previousDate!)})
                </Text>
              )}
            </Alert>
          )}

          <Textarea
            label="Muistiinpanot (valinnainen)"
            placeholder="Lisätietoja..."
            rows={3}
            {...form.getInputProps('notes')}
          />
        </Stack>
      </form>
    </AppModal>
  )
}
